import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchResource } from '@app/src/api/fetchHooks'
import { useUpdateResource } from '@app/src/api/updateHooks'
import type RequestError from '@app/src/errors/RequestError'
import useApi from '@app/src/hooks/api'
import useErrorNotification from '@app/src/hooks/errorNotification'
import { TabularImportQueryKey } from '@app/src/pages/TabularImport/shared/tabularImportHooks'
import type { CategoryPlan } from '@app/src/types/categoryPlan'
import type { ImportSession } from '@app/src/types/tabularImport'
import { useMutation, useQueryClient } from '@tanstack/react-query'

export const useNewProviderIds = (sessionId: string | null) => {
  const query = useFetchResource<{ providerIds: number[] }>({
    key: [FetchKey.TabularImportNewProviderIds, sessionId ?? ''],
    endpoint: endpoints.tabularImportNewProviderIds(sessionId ?? ''),
    options: {
      enabled: Boolean(sessionId),
      staleTime: Infinity,
    },
  })

  return { ...query, data: query.data?.providerIds }
}

// =====================================================
// Category Plan
// =====================================================

export const useUpdateCategoryPlan = (sessionId: string | null) => {
  const queryClient = useQueryClient()
  const { showErrorNotification } = useErrorNotification()
  const { mutate: saveCategoryPlan, isPending: isUpdatingCategoryPlan } = useUpdateResource<
    { message: string },
    CategoryPlan
  >()

  const updateCategoryPlan = (plan: CategoryPlan) => {
    saveCategoryPlan(
      {
        url: endpoints.tabularImportCategoryPlan(sessionId ?? ''),
        body: plan,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: TabularImportQueryKey.status(sessionId) })
        },
        onError: error => showErrorNotification({ requestError: error }),
      },
    )
  }

  return { updateCategoryPlan, isUpdatingCategoryPlan }
}

type ConvertCategoryToColumnsParams = { sessionId: string; sourceColumnNames: string[] }

export const useConvertCategoryToColumns = () => {
  const { post } = useApi()
  const queryClient = useQueryClient()
  const { showErrorNotification } = useErrorNotification()

  return useMutation({
    mutationFn: ({ sessionId, sourceColumnNames }: ConvertCategoryToColumnsParams) =>
      post<ImportSession, { sourceColumnNames: string[] }>({
        url: endpoints.tabularImportCategoryToColumns(sessionId),
        body: { sourceColumnNames },
      }),
    onSuccess: (data, { sessionId }) => {
      queryClient.setQueryData(TabularImportQueryKey.status(sessionId), data)
    },
    onError: (error: RequestError) =>
      showErrorNotification({ requestError: error, specificFallbackErrorMessage: error.body?.message }),
  })
}

type ConvertColumnToCategoryParams = {
  sessionId: string
  mappingId: number
  categoryName?: string
  existingCategoryId?: number
}

export const useConvertColumnToCategory = () => {
  const { post } = useApi()
  const queryClient = useQueryClient()
  const { showErrorNotification } = useErrorNotification()

  return useMutation({
    mutationFn: ({ sessionId, mappingId, categoryName, existingCategoryId }: ConvertColumnToCategoryParams) =>
      post<ImportSession, { categoryName?: string; existingCategoryId?: number }>({
        url: endpoints.tabularImportMappingConvertToCategory(sessionId, mappingId),
        body: { categoryName, existingCategoryId },
      }),
    onSuccess: (data, { sessionId }) => {
      queryClient.setQueryData(TabularImportQueryKey.status(sessionId), data)
    },
    onError: (error: RequestError) =>
      showErrorNotification({ requestError: error, specificFallbackErrorMessage: error.body?.message }),
  })
}
